/**
 * Bulk Actions Toolbar Component
 * Shows action buttons when multiple findings are selected
 */

import { Group, Button, Text, Paper, Select } from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";
import { useState } from "react";
import { useBulkUpdateFindings } from "../../hooks/useAuditAnnotations";
import { notifications } from "@mantine/notifications";
import { modals } from "@mantine/modals";
import { FindingStatus } from "../../types/audit";

interface BulkActionsToolbarProps {
  selectedIds: string[];
  onClearSelection: () => void;
  onSuccess?: () => void;
}

const STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "acknowledged", label: "Acknowledged" },
  { value: "in_progress", label: "In Progress" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
];

export function BulkActionsToolbar({
  selectedIds,
  onClearSelection,
  onSuccess,
}: BulkActionsToolbarProps) {
  const [selectedStatus, setSelectedStatus] = useState<FindingStatus | null>(
    null,
  );
  const bulkUpdate = useBulkUpdateFindings();

  const handleBulkUpdate = () => {
    if (!selectedStatus) {
      notifications.show({
        title: "Status Required",
        message: "Please select a status before updating",
        color: "yellow",
      });
      return;
    }

    modals.openConfirmModal({
      title: `Update ${selectedIds.length} Finding${selectedIds.length > 1 ? "s" : ""}`,
      children: (
        <Text size="sm">
          Are you sure you want to update {selectedIds.length} finding
          {selectedIds.length > 1 ? "s" : ""} to{" "}
          <strong>{selectedStatus}</strong> status?
        </Text>
      ),
      labels: { confirm: "Update", cancel: "Cancel" },
      confirmProps: { color: "blue" },
      onConfirm: async () => {
        try {
          const result = await bulkUpdate.mutateAsync({
            finding_ids: selectedIds,
            status: selectedStatus,
          });

          notifications.show({
            title: "Findings Updated",
            message: `Successfully updated ${result.updated_count} finding${result.updated_count > 1 ? "s" : ""}`,
            color: "green",
          });

          onClearSelection();
          onSuccess?.();
        } catch {
          // error toast comes from the global mutation handler (US-081)
        }
      },
    });
  };

  if (selectedIds.length === 0) {
    return null;
  }

  return (
    <Paper p="md" withBorder shadow="sm" mb="md" bg="var(--mantine-color-blue-light)">
      <Group justify="space-between">
        <Group gap="md">
          <Text size="sm" fw={600}>
            {selectedIds.length} finding{selectedIds.length > 1 ? "s" : ""}{" "}
            selected
          </Text>

          <Select
            placeholder="Change status to..."
            data={STATUS_OPTIONS}
            value={selectedStatus}
            onChange={(value) => setSelectedStatus(value as FindingStatus)}
            w={200}
            size="sm"
            clearable
          />

          <Button
            variant="filled"
            color="blue"
            size="sm"
            leftSection={<IconCheck size={16} />}
            onClick={handleBulkUpdate}
            loading={bulkUpdate.isPending}
            disabled={!selectedStatus}
          >
            Update Status
          </Button>
        </Group>

        <Button
          variant="subtle"
          color="gray"
          size="sm"
          leftSection={<IconX size={16} />}
          onClick={onClearSelection}
        >
          Clear Selection
        </Button>
      </Group>
    </Paper>
  );
}
